import React, { useCallback } from 'react';
import { Heading } from '../../../Heading/Heading';
import { useSonTranslation } from '../../../../../translation/useSonTranslation';
import handleCopy from '../utils/handleCopy';
import { IConnectedPage } from '../../../../../types/api/connectedPages/IConnectedPage';
import { createExtender } from '../../../../../lib/bemUtils/bem';
import { getHexColor } from '../../../../../types/ICategory';
import { categoriesToString } from '../utils/Helper';
import { IDimensions } from '../interfaces/IConnectedPagesGraphProperties';
import { defaultDimensions } from '../DefaultDimensions';
import ILink from '../../../../../types/ILink';
import { ellipsis } from '../../../../StringUtils';

type ConnectedPagesGraphBreadCrumbProps = {
  page: IConnectedPage | ILink;
  headlineLevel: number;
  dimension?: IDimensions;
  isClickable: boolean;
  onClickNode: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, url: string) => void;
};

const isIConnectedPage = (page: IConnectedPage | ILink): page is IConnectedPage => 'pageLink' in page;

export function ConnectedPagesGraphBreadCrumb({
  page,
  headlineLevel,
  dimension = defaultDimensions,
  isClickable,
  onClickNode,
}: ConnectedPagesGraphBreadCrumbProps) {
  const { t } = useSonTranslation('ConnectedPagesGraph');

  const isConnectedPage = isIConnectedPage(page);
  const name = isConnectedPage ? page.pageLink.name : page.name;
  const url = isConnectedPage ? page.pageLink.url : page.url;
  const categories = isConnectedPage ? page.categories : undefined;

  const copyHandler = useCallback(() => handleCopy(name), [name]);

  return (
    <nav className="connected-pages-graph__breadcrumb" aria-labelledby="connected-pages-graph-breadcrumb">
      <Heading hierarchy={headlineLevel} className="invisible" id="connected-pages-graph-breadcrumb">
        {t('breadcrumbHeadline')}
      </Heading>
      <ol>
        <li key={url}>
          <div
            className="connected-pages-graph__parent"
            style={{
              width: dimension.parentPage.width,
              height: dimension.parentPage.width,
            }}
          >
            {isClickable
              ? (
                  <a
                    href={`#${url}`}
                    className={createExtender('connected-pages-graph__parent-title')([
                      'connected-pages-graph__parent-title--link',
                    ])}
                    onClick={event => onClickNode(event, url)}
                    aria-label={name}
                    onCopy={copyHandler}
                    style={{ borderColor: getHexColor(categories) }}
                  >
                    {ellipsis(name, 25)}
                    {categoriesToString(categories)}
                  </a>
                )
              : (
                  <span className="connected-pages-graph__parent-title" onCopy={copyHandler}>
                    {ellipsis(name, 25)}
                    {categoriesToString(categories)}
                  </span>
                )}
          </div>
        </li>
      </ol>
    </nav>
  );
}
